import { useEffect, useState } from 'react';
import {
  getBankInvoiceConfig,
  listBankInvoices,
  createBankInvoice,
  downloadBankInvoicePdf,
} from '../api.js';

import './BankInvoicePanel.css';

export default function BankInvoicePanel() {
  const [config, setConfig] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [name, setName] = useState('');
  const [inn, setInn] = useState('');
  const [kpp, setKpp] = useState('');
  const [channels, setChannels] = useState(1);
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [requestKey, setRequestKey] = useState(null);

  useEffect(() => {
    let active = true;

    Promise.all([
      getBankInvoiceConfig(),
      listBankInvoices(),
    ]).then(([settings, history]) => {
      if (!active) return;
      setConfig(settings);
      setInvoices(Array.isArray(history) ? history : []);
    }).catch((exc) => {
      if (active) {
        setError(exc?.message || 'Не удалось загрузить счета');
      }
    });

    return () => { active = false; };
  }, []);

  function changed() {
    setRequestKey(null);
    setError('');
  }

  async function create(event) {
    event.preventDefault();
    if (busy || !config?.available) return;

    setBusy(true);
    setError('');

    const key = requestKey || crypto.randomUUID();
    setRequestKey(key);

    try {
      const invoice = await createBankInvoice({
        request_key: key,
        channels: Number(channels),
        legal_name: name.trim(),
        inn: inn.trim(),
        kpp: kpp.trim(),
        offer_accepted: accepted,
      });

      await downloadBankInvoicePdf(invoice.id);
      setRequestKey(null);
      setInvoices(await listBankInvoices());
    } catch (exc) {
      setError(exc?.message || 'Не удалось создать счёт');
    } finally {
      setBusy(false);
    }
  }

  async function download(invoice) {
    setError('');
    try {
      await downloadBankInvoicePdf(invoice.id);
    } catch (exc) {
      setError(exc?.message || 'Не удалось скачать счёт');
    }
  }

  return (
    <section className="mf-bank-invoice">
      <h2>Оплата по банковскому счёту</h2>
      <p>
        Сформируйте счёт для юридического лица или ИП.
        Документ скачивается автоматически. Скачивание
        не считается оплатой и не активирует каналы.
      </p>

      {config && !config.available && (
        <p role="status">
          Выставление счетов пока недоступно.
        </p>
      )}

      {config?.available && (
        <form onSubmit={create}>
          <div className="mf-bank-invoice-grid">
            <label>
              Наименование организации или ИП
              <input
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  changed();
                }}
                maxLength={240}
                required
              />
            </label>
            <label>
              ИНН
              <input
                value={inn}
                onChange={(e) => {
                  setInn(e.target.value.replace(/\D/g, ''));
                  changed();
                }}
                pattern="[0-9]{10}([0-9]{2})?"
                maxLength={12}
                inputMode="numeric"
                required
              />
            </label>
            <label>
              КПП (для юридических лиц)
              <input
                value={kpp}
                onChange={(e) => {
                  setKpp(e.target.value.replace(/\D/g, ''));
                  changed();
                }}
                pattern="([0-9]{9})?"
                maxLength={9}
                inputMode="numeric"
              />
            </label>
            <label>
              Количество каналов
              <input
                type="number"
                min="1"
                max="1000"
                value={channels}
                onChange={(e) => {
                  setChannels(e.target.value);
                  changed();
                }}
                required
              />
            </label>
          </div>
          <p className="mf-bank-invoice-total">
            К оплате: {
              (
                Number(channels || 0)
                * Number(config.unit_price_rub)
              ).toLocaleString('ru-RU')
            } ₽
          </p>
          <label className="mf-bank-invoice-consent">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => {
                setAccepted(e.target.checked);
                changed();
              }}
              required
            />
            <span>
              Принимаю{' '}
              <a
                href={config.offer_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                условия оферты
              </a>
            </span>
          </label>
          <button type="submit" disabled={busy}>
            {busy ? 'Формируем счёт…' : 'Скачать счёт PDF'}
          </button>
        </form>
      )}

      {error && <p role="alert">{error}</p>}

      {invoices.length > 0 && (
        <div className="mf-bank-invoice-history">
          <h3>Ранее выставленные счета</h3>
          {invoices.map((invoice) => (
            <div key={invoice.id}>
              <span>{invoice.number}</span>
              <span>
                {(invoice.total_kopeks / 100)
                  .toLocaleString('ru-RU')} ₽
              </span>
              <button
                type="button"
                onClick={() => download(invoice)}
              >
                Скачать повторно
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
